import { useState, useCallback, useRef, useEffect } from 'react';
import type { Message } from '../types';
import { recordEvent } from '@workspace/observability/browser';

/**
 * Reconnect policy for the realtime voice socket.
 *
 * The s2s backend is lazy-started by the gateway, so the very first connection
 * after an idle period can arrive while the service is still starting. Until the
 * process binds its port the gateway answers with 503, and a 503 to a WebSocket
 * upgrade means no upgrade at all — which the browser surfaces as an opaque
 * `code=1006` with no reason. Retrying with backoff turns that dead click into a
 * short wait.
 *
 * `@workspace/network` can't help here: its retry/backoff lives in the fetch
 * client and keys off `response.status`, which a WebSocket never exposes.
 */
const RECONNECT_MAX_ATTEMPTS = 6;
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 8_000;

/** WS close codes that mean "don't bother retrying". */
const NON_RETRYABLE_CLOSE_CODES = new Set([
  1000, // normal
  1005, // no status (treated as a clean close by the old code)
  1008, // policy violation
  4029, // our own concurrent-session-limit close
]);

/**
 * Exponential backoff with full jitter. Jitter matters because several tabs (or
 * a remounting component) would otherwise retry in lockstep and hammer the
 * service at the exact moment it's trying to start.
 */
function reconnectDelay(attempt: number): number {
  const exponential = Math.min(
    RECONNECT_BASE_DELAY_MS * 2 ** attempt,
    RECONNECT_MAX_DELAY_MS
  );
  return Math.round(exponential * (0.5 + Math.random() * 0.5));
}

interface RealtimeVoiceConfig {
  baseURL: string;
  model?: string;
  /** TTS voice id (s2s voice catalog). Undefined → engine default. */
  voice?: string;
  /** When true, only transcribe without triggering LLM response */
  disableLLM?: boolean;
  onMessageUpdate?: (message: Partial<Message>) => void;
  onComplete?: (message: Message) => void;
  onUserTranscription?: (transcript: string) => void;
  /** Called with preview transcription (fast, lower accuracy) that will be replaced by final */
  onUserTranscriptionPreview?: (preview: string) => void;
}

export function useRealtimeVoice({
  baseURL,
  model = 'gpt-4o-realtime-preview-2024-10-01',
  voice,
  disableLLM = false,
  onMessageUpdate,
  onComplete,
  onUserTranscription,
  onUserTranscriptionPreview,
}: RealtimeVoiceConfig) {
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  /**
   * True while the backend is reachable but its speech models are still loading,
   * or while we're backing off between connection attempts. Distinct from
   * `isConnected`: the socket may be open but not yet able to serve audio, and
   * the server deliberately withholds `session.created` until it can.
   */
  const [isVoiceLoading, setIsVoiceLoading] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Set by disconnect()/unmount so an in-flight retry doesn't resurrect the socket. */
  const closedByUserRef = useRef(false);
  /** Resolvers for callers awaiting a usable session (see `waitForSession`). */
  const sessionWaitersRef = useRef<Array<(ok: boolean) => void>>([]);
  // Mirrors of the state above, so callbacks can read current values without
  // taking a dependency on them (which would re-create the socket handlers).
  const isConnectedRef = useRef(false);
  const isVoiceLoadingRef = useRef(false);
  isConnectedRef.current = isConnected;
  isVoiceLoadingRef.current = isVoiceLoading;
  const audioContextRef = useRef<AudioContext | null>(null);
  const playbackContextRef = useRef<AudioContext | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioQueueRef = useRef<ArrayBuffer[]>([]);
  const isPlayingRef = useRef<boolean>(false);
  const nextPlayTimeRef = useRef<number>(0);
  const chunkIndexRef = useRef<number>(0);
  const playAudioQueueRef = useRef<() => void>(() => {});
  const currentResponseIdRef = useRef<string | null>(null);
  const currentMessageIdRef = useRef<string | null>(null);
  const currentContentRef = useRef<string>('');
  // Latest resolved voice, read at session.update time (and pushed on change
  // mid-session) so persona/default voice changes take effect without reconnect.
  const voiceRef = useRef(voice);
  voiceRef.current = voice;

  /** Resolve everyone waiting on a usable session, then clear the queue. */
  const settleSessionWaiters = useCallback((ok: boolean) => {
    const waiters = sessionWaitersRef.current;
    sessionWaitersRef.current = [];
    for (const waiter of waiters) waiter(ok);
  }, []);

  // Handle events from server
  const handleServerEvent = useCallback(
    (event: any) => {
      switch (event.type) {
        case 'session.warming':
          // Server accepted the socket but its speech models are still loading.
          // It withholds `session.created` until they're ready, so stay in a
          // warming state rather than reporting the session as live.
          setIsVoiceLoading(true);
          break;

        case 'session.created':
          // The server only sends this once speech services can actually serve
          // audio — this is the real "voice is live" signal.
          setIsVoiceLoading(false);
          settleSessionWaiters(true);
          break;

        case 'session.updated':
          break;

        case 'conversation.item.input_audio_transcription.delta':
          // Delta events contain preview transcription from fast Moonshine VAD
          // The delta is the FULL preview text (not incremental), which will be
          // replaced when the accurate Whisper transcription arrives via 'completed'
          if (event.delta && !event.delta.startsWith('[echo cancelled]')) {
            onUserTranscriptionPreview?.(event.delta);
          }
          break;

        case 'conversation.item.input_audio_transcription.completed':
          // Completed events contain the final accurate transcription from Whisper
          // This replaces any preview that was shown from delta events
          if (
            event.transcript &&
            !event.transcript.startsWith('[echo cancelled]')
          ) {
            onUserTranscription?.(event.transcript);
          }
          break;

        case 'response.created':
          if (playbackContextRef.current) {
            playbackContextRef.current.close();
            playbackContextRef.current = null;
            audioQueueRef.current = [];
            nextPlayTimeRef.current = 0;
            chunkIndexRef.current = 0;
          }
          currentResponseIdRef.current = event.response?.id || null;
          currentMessageIdRef.current = `assistant-${Date.now()}`;
          currentContentRef.current = '';
          break;

        case 'response.output_text.delta':
          if (event.delta && currentMessageIdRef.current) {
            currentContentRef.current += event.delta;
            onMessageUpdate?.({
              id: currentMessageIdRef.current,
              role: 'assistant',
              content: currentContentRef.current,
            });
          }
          break;

        case 'response.output_text.done':
          if (event.text && currentMessageIdRef.current) {
            currentContentRef.current = event.text;
            const finalMessage = {
              id: currentMessageIdRef.current,
              role: 'assistant',
              content: event.text,
            } as Message;
            onComplete?.(finalMessage);
          }
          break;

        case 'response.audio.delta':
          if (event.delta) {
            const audioData = base64ToArrayBuffer(event.delta);
            audioQueueRef.current.push(audioData);
            playAudioQueueRef.current();
          }
          setIsSpeaking(true);
          break;

        case 'response.audio.done':
          nextPlayTimeRef.current = 0;
          chunkIndexRef.current = 0;
          setIsSpeaking(false);
          break;

        case 'response.done':
          setIsSpeaking(false);
          currentResponseIdRef.current = null;
          currentMessageIdRef.current = null;
          currentContentRef.current = '';
          break;

        case 'error':
          // The meaningful payload is event.error ({ type, code, message }).
          // Log it explicitly — logging the bare event object renders as `{}`
          // in some consoles and hides the actual cause.
          // `type`/`code` are the server's own classification and are bounded;
          // `message` is free text and stays in the console line. Deliberately
          // NOT recorded at the socket's onerror handler a few lines below —
          // that fires without a usable payload, and the close handler already
          // reports the same failure with a close code attached.
          recordEvent(
            'chat.voice.server_error',
            {
              error_type: String(event.error?.type ?? 'unknown'),
              error_code: String(event.error?.code ?? 'unknown'),
            },
            { error: true },
          );
          console.error(
            'Server error:',
            event.error?.message ?? event.error ?? event
          );
          break;
      }
    },
    [
      onMessageUpdate,
      onComplete,
      onUserTranscription,
      onUserTranscriptionPreview,
      settleSessionWaiters,
    ]
  );

  // Initialize WebSocket connection
  const connect = useCallback(async () => {
    if (!baseURL) return;
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    // A fresh user-initiated connect cancels any pending retry and clears the
    // "user closed this" latch set by disconnect().
    closedByUserRef.current = false;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    const wsURL = `${baseURL}/v1/realtime?model=${encodeURIComponent(model)}`;
    const ws = new WebSocket(wsURL);

    ws.onopen = () => {
      // Connected — reset backoff so a later cold start starts from attempt 0.
      reconnectAttemptRef.current = 0;
      setIsConnected(true);
      ws.send(
        JSON.stringify({
          type: 'session.update',
          session: {
            modalities: ['text', 'audio'],
            instructions:
              'You are a helpful AI assistant. Keep responses concise and conversational.',
            // Resolved voice (persona-assigned → global default → engine default).
            // Omit the field entirely when undefined so the engine picks its own.
            ...(voiceRef.current ? { voice: voiceRef.current } : {}),
            input_audio_format: 'pcm16',
            output_audio_format: 'pcm16',
            input_audio_transcription: {
              model: 'whisper-1',
            },
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 500,
              create_response: !disableLLM, // When disableLLM is true, don't auto-create LLM response
            },
          },
        })
      );
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleServerEvent(data);
    };

    ws.onerror = () => {
      // Browser WebSocket error events carry no detail by design — the
      // actionable info (close code / reason) arrives via onclose below.
      console.error('WebSocket error (see close code/reason for cause)');
    };

    ws.onclose = (event) => {
      setIsConnected(false);
      setIsRecording(false);

      if (wsRef.current === ws) wsRef.current = null;

      const clean = NON_RETRYABLE_CLOSE_CODES.has(event.code);
      const giveUp =
        closedByUserRef.current ||
        clean ||
        reconnectAttemptRef.current >= RECONNECT_MAX_ATTEMPTS;

      if (giveUp) {
        if (!clean && !closedByUserRef.current) {
          // Voice has stopped working and will not recover on its own. The
          // close CODE is the useful half — 1006 is a failed handshake (the
          // gateway's 503 during a lazy start looks exactly like this), while
          // anything else points elsewhere entirely — and the retry count says
          // whether it died immediately or fought for it. Both are in scope
          // here and were going only to the console.
          recordEvent(
            'chat.voice.gave_up',
            { close_code: event.code, retries: reconnectAttemptRef.current },
            { error: true },
          );
          console.error(
            `WebSocket closed unexpectedly: code=${event.code}` +
              (event.reason ? ` reason="${event.reason}"` : '') +
              ` (gave up after ${reconnectAttemptRef.current} retries)`
          );
        }
        reconnectAttemptRef.current = 0;
        setIsVoiceLoading(false);
        // Unblock anyone awaiting a session so startRecording() doesn't hang.
        settleSessionWaiters(false);
        return;
      }

      // Retryable: 1006 (failed/aborted handshake — the gateway's 503 during a
      // lazy start looks exactly like this) or 1013 (our own "speech services
      // unavailable, try again later").
      const delay = reconnectDelay(reconnectAttemptRef.current);
      reconnectAttemptRef.current += 1;
      setIsVoiceLoading(true);

      console.warn(
        `Voice socket closed (code=${event.code}${
          event.reason ? `, reason="${event.reason}"` : ''
        }); retry ${reconnectAttemptRef.current}/${RECONNECT_MAX_ATTEMPTS} in ${delay}ms`
      );

      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        if (closedByUserRef.current) return;
        void connectRef.current();
      }, delay);
    };

    wsRef.current = ws;
  }, [baseURL, model, disableLLM, handleServerEvent, settleSessionWaiters]);

  // Stable handle so the retry timer above always calls the latest connect()
  // without making connect() depend on itself.
  const connectRef = useRef(connect);
  connectRef.current = connect;

  /**
   * Resolves once the server confirms a live session (`session.created`), or
   * false if the connection gives up. Replaces the old fixed 500ms sleep, which
   * was both too long on a warm backend and far too short on a cold one.
   */
  const waitForSession = useCallback(
    (timeoutMs = 90_000): Promise<boolean> => {
      if (isConnectedRef.current && !isVoiceLoadingRef.current) {
        return Promise.resolve(true);
      }
      return new Promise<boolean>((resolve) => {
        let settled = false;
        const once = (ok: boolean) => {
          if (settled) return;
          settled = true;
          resolve(ok);
        };
        sessionWaitersRef.current.push(once);
        setTimeout(() => {
          if (settled) return;
          sessionWaitersRef.current = sessionWaitersRef.current.filter(
            (w) => w !== once
          );
          once(false);
        }, timeoutMs);
      });
    },
    []
  );

  // Retune the live session's voice when the resolved voice changes (e.g. the
  // user switches persona mid-conversation) without forcing a reconnect.
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
      JSON.stringify({
        type: 'session.update',
        session: voice ? { voice } : {},
      })
    );
  }, [voice]);

  // Schedule a single audio chunk to play at the right time
  const scheduleAudioChunk = useCallback(
    (ctx: AudioContext, audioData: ArrayBuffer, chunkIndex: number) => {
      const view = new DataView(audioData);
      const numSamples = audioData.byteLength / 2;
      const float32 = new Float32Array(numSamples);

      for (let i = 0; i < numSamples; i++) {
        const int16 = view.getInt16(i * 2, true);
        float32[i] = int16 / 32768.0;
      }

      const audioBuffer = ctx.createBuffer(1, float32.length, 24000);
      audioBuffer.getChannelData(0).set(float32);

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);

      const now = ctx.currentTime;
      const playAt = Math.max(now, nextPlayTimeRef.current);
      const duration = float32.length / 24000;

      source.start(playAt);
      nextPlayTimeRef.current = playAt + duration;

      return { source, endTime: playAt + duration };
    },
    []
  );

  // Play audio queue
  const playAudioQueue = useCallback(() => {
    if (isPlayingRef.current) return;
    if (audioQueueRef.current.length === 0) return;

    if (!playbackContextRef.current) {
      playbackContextRef.current = new AudioContext({ sampleRate: 24000 });
      nextPlayTimeRef.current = 0;
      chunkIndexRef.current = 0;
    }

    const ctx = playbackContextRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    isPlayingRef.current = true;

    while (audioQueueRef.current.length > 0) {
      const audioData = audioQueueRef.current.shift()!;
      scheduleAudioChunk(ctx, audioData, chunkIndexRef.current++);
    }

    isPlayingRef.current = false;

    const timeUntilDone = (nextPlayTimeRef.current - ctx.currentTime) * 1000;
    if (timeUntilDone > 0) {
      setTimeout(() => {
        if (audioQueueRef.current.length === 0) {
          setIsSpeaking(false);
        } else {
          playAudioQueueRef.current();
        }
      }, timeUntilDone + 50);
    }
  }, [scheduleAudioChunk]);

  playAudioQueueRef.current = playAudioQueue;

  // Start recording from microphone
  const startRecording = useCallback(async () => {
    // Wait for a session the server has actually confirmed, rather than a fixed
    // sleep: on a warm backend this returns almost immediately, and on a cold
    // start it waits out the model warmup (plus any reconnects) instead of
    // racing ahead and dropping the first seconds of audio into a dead socket.
    if (
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN ||
      isVoiceLoadingRef.current
    ) {
      await connect();
      const ready = await waitForSession();
      if (!ready) {
        // The user pressed the mic and nothing happened. From their side this
        // is indistinguishable from the button being broken, and it left no
        // trace anywhere — the socket had already given up quietly by this
        // point, so even the close code is gone.
        recordEvent('chat.voice.session_unavailable', {}, { error: true });
        console.error('Voice session unavailable, not starting recording');
        return;
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const audioContext = new AudioContext({ sampleRate: 24000 });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);

      processor.onaudioprocess = (e) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          return;
        }

        const inputData = e.inputBuffer.getChannelData(0);
        const pcm16 = floatTo16BitPCM(inputData);
        const base64 = arrayBufferToBase64(pcm16);

        wsRef.current.send(
          JSON.stringify({
            type: 'input_audio_buffer.append',
            audio: base64,
          })
        );
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      setIsRecording(true);
    } catch (error) {
      // `error.name` separates outcomes with completely different remedies:
      // NotAllowedError is a denied permission (the user can fix it),
      // NotFoundError is no microphone at all, and anything else is a genuine
      // fault. Low-cardinality and already computed by the browser.
      recordEvent(
        'chat.voice.mic_failed',
        { error_name: String((error as { name?: string })?.name ?? 'unknown') },
        // A denied permission is a choice, not a fault — flagging it would fill
        // error rates with users declining the prompt.
        { error: (error as { name?: string })?.name !== 'NotAllowedError' },
      );
      console.error('Failed to start recording:', error);
    }
  }, [connect, waitForSession]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'input_audio_buffer.commit',
        })
      );
    }

    setIsRecording(false);
  }, []);

  // Toggle recording
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Disconnect
  const disconnect = useCallback(() => {
    stopRecording();

    // Latch first: onclose fires synchronously for an OPEN socket, and without
    // this it would schedule a reconnect for a socket we're deliberately closing.
    closedByUserRef.current = true;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptRef.current = 0;
    settleSessionWaiters(false);

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (playbackContextRef.current) {
      playbackContextRef.current.close();
      playbackContextRef.current = null;
    }

    audioQueueRef.current = [];
    nextPlayTimeRef.current = 0;
    setIsConnected(false);
    setIsVoiceLoading(false);
  }, [stopRecording, settleSessionWaiters]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isRecording,
    isSpeaking,
    /** Backend reachable but speech models still loading, or retrying a connect. */
    isVoiceLoading,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    toggleRecording,
  };
}

// Helper functions
function floatTo16BitPCM(float32Array: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(float32Array.length * 2);
  const view = new DataView(buffer);

  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return buffer;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const buffer = new ArrayBuffer(len);
  const bytes = new Uint8Array(buffer);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return buffer;
}
